<template>
	<AppBottomSheet :active="active" :forceWidth="bottomSheetWidth" @toggling-popup="v => $emit('toggling-popup', v)">
		<div v-if="active" class="newsletter-schedule">
			<ModalHeader
				:title="getString('schedule_newsletter')"
				:button-text="getString('schedule_it')"
				:button-loading="scheduling"
				:button-disabled="!scheduledDate"
				@click-button="handleSchedule"
			/>

			<p class="schedule-subtitle">{{ getString('newsletter_will_be_scheduled_for_x_recipients_y_credits', [recipientsCount.toLocaleString(), creditsRequired]) }}</p>

			<div class="calendar-container">
				<DatePicker
					ref="date-picker"
					v-model="scheduledDate"
					mode="datetime"
					is24hr
					color="teal"
					:min-date="minDate"
					:max-date="maxDate"
					:is-dark="userStore.darkMode"
					:is-expanded="true"
				/>
			</div>
		</div>
		<RechargeBottomSheet
			:active="showRechargeBottomSheet"
			@close="handleRechargeClose"
		/>
	</AppBottomSheet>
</template>

<script>
import { useLanguageStore, useNotificationStore } from '@/shared/stores';
import { useUserStore, useNewsletterStore } from '@/console-new/stores';
import AppBottomSheet from '@/shared/components/AppBottomSheet.vue';
import AppButton from '@/shared/components/AppButton.vue';
import ModalHeader from '@/console-new/components/shared/ModalHeader.vue';
import RechargeBottomSheet from '@/console-new/components/shared/RechargeBottomSheet.vue';
import { DatePicker } from 'v-calendar';
import WindowWidth from '@/console-new/mixins/WindowWidth';
import API from '@/shared/helpers/API';
import {localToServerTime} from "@/shared/helpers/dates";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const newsletterStore = useNewsletterStore();
		const userStore = useUserStore();
		return {
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification,
			userStore,
			newsletterStore
		};
	},
	name: "NewsletterSchedule",
	components: {
		AppBottomSheet,
		AppButton,
		ModalHeader,
		RechargeBottomSheet,
		DatePicker
	},
	mixins: [WindowWidth],
	emits: ['toggling-popup', 'close'],
	props: {
		active: {
			type: Boolean,
			required: true
		},
		recipientsCount: {
			type: Number,
			required: true
		},
		selectedSections: {
			type: Array,
			required: true
		}
	},
	data() {
		return {
			scheduledDate: this.getDefaultDate(),
			scheduling: false,
			minDate: new Date(),
			maxDate: this.getMaxDate(),
			showRechargeBottomSheet: false
		};
	},
	computed: {
		creditsRequired() {
			return Math.ceil(this.recipientsCount / 2);
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		}
	},
	methods: {
		getDefaultDate() {
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);
			tomorrow.setHours(9, 0, 0, 0);
			return tomorrow;
		},
		getMaxDate() {
			const oneYearFromNow = new Date();
			oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
			return oneYearFromNow;
		},
		handleRechargeClose(success) {
			this.showRechargeBottomSheet = false;
			if (success) {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}
		},
		handleSchedule() {
			if (!this.scheduledDate) {
				this.showNotification('generic-error');
				return;
			}

			if (this.scheduling) {
				return;
			}

			this.scheduling = true;

			API.init().sendNewsletter(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				this.selectedSections,
				this.scheduledDate ? localToServerTime(this.scheduledDate) : null,
				(response) => {
					this.scheduling = false;

					if (response.result === 'OK') {
						this.showNotification('newsletter-scheduled-success');
						this.newsletterStore.editingNewsletterId = null;
						// Refresh newsletter list to show updated status
						this.newsletterStore.businessNewsletters = null;
						this.newsletterStore.getBusinessNewsletters();
						this.$emit('close');
					} else {
						if (response.details === 'content_rejected') {
							const reason = response.reason || 'Content did not pass moderation';
							this.showNotification('newsletter-content-rejected', [reason]);
						} else if (response.details === 'newsletter_pending') {
							this.showNotification('newsletter-pending');
						} else if (response.details === 'no_recipients') {
							this.showNotification('no-recipients-found');
						} else if (response.details === 'business_link_not_configured') {
							this.showNotification('business-link-not-configured');
						} else if (response.details === 'insufficient_credits') {
							this.showRechargeBottomSheet = true;
							const requiredCredits = response.required_credits || this.creditsRequired;
							this.showNotification('insufficient-credits-for-newsletter', [requiredCredits]);
						} else {
							this.showNotification('generic-error');
						}
					}
				}
			);
		}
	},
	mounted() {
		if (!this.scheduledDate) {
			this.scheduledDate = this.getDefaultDate();
		}
	},
	watch: {
		active(newVal) {
			if (!newVal) {
				// Reset date when modal closes
				this.scheduledDate = null;
			}
		}
	}
}
</script>

<style scoped lang="scss">
.newsletter-schedule {
	.schedule-subtitle {
		font-size: 14px;
		color: var(--color-txt-1);
		margin-bottom: 20px;
	}

	.calendar-container {
		display: flex;
		justify-content: center;

		:deep(.vc-container) {
			border: 1px solid var(--color-border-2);
			border-radius: 13px;
			background-color: transparent;
		}
	}
}
</style>
